<div class="container-xxl py-6 manual" data-testid="manual-page">
  <!-- Hero: what this page is, and the two ways to use it. -->
  <div class="card manual-hero mb-6">
    <div class="card-body">
      <div class="d-flex flex-wrap align-items-start justify-content-between gap-5">
        <div class="manual-hero__intro">
          <!-- The site's own eyebrow rather than a `badge-light-primary`,
               whose text colour is set with `!important` for a white ground and
               so came out brand-ink on a translucent pill over ink — an empty
               grey capsule with the label invisible inside it. -->
          <span class="eyebrow mb-4"><span class="dot"></span>{{ "MANUAL.BADGE" | translate }}</span>
          <h1 class="fs-2hx fw-bold mb-3" data-testid="manual-heading">
            {{ "MANUAL.TITLE" | translate }}
          </h1>
          <p class="fs-5 text-muted mb-0">{{ "MANUAL.SUBTITLE" | translate }}</p>
        </div>

        <div class="manual-mode btn-group" role="group" [attr.aria-label]="'MANUAL.MODE' | translate">
          <button
            type="button"
            class="btn btn-sm"
            [class.btn-primary]="mode() === 'read'"
            [class.btn-light]="mode() !== 'read'"
            data-testid="manual-mode-read"
            (click)="setMode('read')"
          >
            <app-keenicon name="book-open" class="fs-4 me-2"></app-keenicon>
            {{ "MANUAL.MODE_READ" | translate }}
          </button>
          <button
            type="button"
            class="btn btn-sm"
            [class.btn-primary]="mode() === 'video'"
            [class.btn-light]="mode() !== 'video'"
            data-testid="manual-mode-video"
            (click)="setMode('video')"
          >
            <app-keenicon name="youtube" class="fs-4 me-2"></app-keenicon>
            {{ "MANUAL.MODE_VIDEO" | translate }}
          </button>
        </div>
      </div>

      <div class="d-flex flex-wrap align-items-center gap-3 mt-6">
        <div class="manual-search position-relative flex-grow-1">
          <!-- app-keenicon's host is `display: contents`, so a class on the
               component positions nothing — the wrapper is what gets placed. -->
          <span class="manual-search__icon position-absolute d-flex align-items-center top-50 translate-middle-y">
            <app-keenicon name="magnifier" class="fs-3"></app-keenicon>
          </span>
          <input
            type="search"
            class="form-control form-control-solid ps-13"
            [placeholder]="'MANUAL.SEARCH' | translate"
            [ngModel]="query()"
            (ngModelChange)="query.set($event)"
            data-testid="manual-search"
          />
        </div>
        <button type="button" class="btn btn-sm btn-light" (click)="expandAll()">
          {{ "MANUAL.EXPAND_ALL" | translate }}
        </button>
        <button type="button" class="btn btn-sm btn-light" (click)="collapseAll()">
          {{ "MANUAL.COLLAPSE_ALL" | translate }}
        </button>
      </div>
    </div>
  </div>

  <div class="row g-6">
    <!-- Contents. Sticky on desktop so the map stays put while the reader
         scrolls a long module; hidden on small screens, where the collapsed
         module bars already are the map. -->
    <div class="col-lg-3 d-none d-lg-block">
      <div class="card manual-toc position-sticky">
        <div class="card-body">
          <h2 class="fs-6 fw-semibold text-muted text-uppercase mb-4">
            {{ "MANUAL.CONTENTS" | translate }}
          </h2>
          <ul class="manual-toc__list m-0 p-0 d-flex flex-column gap-1">
            @for (mod of resolved(); track mod.def.id) {
              <li>
                <button
                  type="button"
                  class="manual-toc__link d-flex align-items-center gap-3 w-100 text-start"
                  [class.is-open]="isOpen(mod.def.id)"
                  (click)="openFrom(mod.def.id)"
                >
                  <app-keenicon [name]="mod.def.icon" class="fs-4"></app-keenicon>
                  <span class="flex-grow-1 text-start">{{ moduleTitle(mod.def) }}</span>
                  <span class="badge badge-light">{{ mod.steps.length }}</span>
                </button>
              </li>
            }
          </ul>
        </div>
      </div>
    </div>

    <div class="col-lg-9">
      @for (mod of resolved(); track mod.def.id; let modIndex = $index) {
        <section class="card manual-module overflow-hidden mb-5" [id]="'manual-' + mod.def.id">
          <button
            type="button"
            class="manual-module__head d-flex align-items-center gap-4 w-100 p-6 text-start"
            [attr.aria-expanded]="isOpen(mod.def.id)"
            [attr.data-testid]="'manual-module-' + mod.def.id"
            (click)="toggle(mod.def.id)"
          >
            <span class="manual-module__icon d-grid flex-shrink-0">
              <app-keenicon [name]="mod.def.icon" class="fs-2x"></app-keenicon>
            </span>
            <span class="manual-module__text d-flex flex-column flex-grow-1">
              <span class="manual-module__title fs-5 fw-bold">
                {{ modIndex + 1 }}. {{ moduleTitle(mod.def) }}
              </span>
              <span class="manual-module__desc">{{ moduleDesc(mod.def) }}</span>
            </span>
            <span class="badge badge-light-primary flex-shrink-0">
              {{ mod.steps.length }} {{ "MANUAL.STEPS" | translate }}
            </span>
            <app-keenicon
              name="down"
              class="fs-2 manual-module__chevron"
              [class.is-open]="isOpen(mod.def.id)"
            ></app-keenicon>
          </button>

          @if (isOpen(mod.def.id)) {
            <div class="card-body pt-0">
              @if (mode() === "video") {
                <!-- Video mode. Same module, same order — a two-minute version
                     for someone who would rather be shown than told. -->
                <div class="manual-video d-grid gap-6 pt-4">
                  <video
                    class="manual-video__player w-100"
                    controls
                    preload="none"
                    playsinline
                    [poster]="poster(mod)"
                    [attr.data-testid]="'manual-video-' + mod.def.id"
                  >
                    <source [src]="video(mod.def)" type="video/mp4" />
                  </video>

                  <div class="manual-video__side">
                    <h3 class="fs-4 fw-semibold mb-3">{{ "MANUAL.IN_THIS_VIDEO" | translate }}</h3>
                    <p class="text-muted fs-7 mb-4">{{ moduleVideoNote(mod.def) }}</p>
                    <ol class="manual-video__chapters m-0">
                      @for (step of mod.steps; track step.def.id) {
                        <li>{{ stepTitle(mod.def, step.def) }}</li>
                      }
                    </ol>
                    <div class="d-flex flex-wrap gap-3 mt-5">
                      <button type="button" class="btn btn-sm btn-light-primary" (click)="setMode('read')">
                        {{ "MANUAL.READ_INSTEAD" | translate }}
                      </button>
                      <a class="btn btn-sm btn-light" [routerLink]="mod.def.route">
                        {{ "MANUAL.TRY_IT" | translate }}
                      </a>
                    </div>
                  </div>
                </div>
              } @else {
                @for (step of mod.steps; track step.def.id; let stepIndex = $index) {
                  <div class="manual-step" [class.manual-step--first]="stepIndex === 0">
                    <div class="row g-5 align-items-start">
                      <div class="col-xl-7">
                        <figure class="manual-shot position-relative d-block overflow-hidden mb-0">
                          <img
                            class="manual-shot__img w-100 h-auto d-block"
                            [src]="step.image"
                            [alt]="stepTitle(mod.def, step.def)"
                            loading="lazy"
                            decoding="async"
                          />
                          @for (mark of step.marks; track mark.key) {
                            <span
                              class="manual-shot__box position-absolute"
                              [class.is-active]="activeMark() === stepKey(mod.def, step) + ':' + mark.key"
                              [class.is-edge-top]="mark.y < 4"
                              [class.is-edge-left]="mark.x < 3"
                              [style.left.%]="mark.x"
                              [style.top.%]="mark.y"
                              [style.width.%]="mark.w"
                              [style.height.%]="mark.h"
                            >
                              <span class="manual-shot__pin position-absolute d-grid rounded-circle fs-8 fw-bolder lh-1">{{ mark.index }}</span>
                            </span>
                          }
                        </figure>
                      </div>

                      <div class="col-xl-5">
                        <span class="manual-step__index d-inline-block fw-bold">
                          {{ "MANUAL.STEP" | translate }} {{ stepIndex + 1 }}
                        </span>
                        <h3 class="fs-3 fw-bold mt-2 mb-3">{{ stepTitle(mod.def, step.def) }}</h3>
                        <p class="text-muted mb-5">{{ stepBody(mod.def, step.def) }}</p>

                        @if (step.marks.length) {
                          <ul class="manual-marks m-0 p-0 d-flex flex-column gap-2">
                            @for (mark of step.marks; track mark.key) {
                              <li
                                class="manual-marks__item d-flex align-items-start gap-3"
                                (mouseenter)="activeMark.set(stepKey(mod.def, step) + ':' + mark.key)"
                                (mouseleave)="activeMark.set(null)"
                                (focusin)="activeMark.set(stepKey(mod.def, step) + ':' + mark.key)"
                                (focusout)="activeMark.set(null)"
                                tabindex="0"
                              >
                                <span class="manual-marks__num d-grid flex-shrink-0 rounded-circle fw-bolder">{{ mark.index }}</span>
                                <span>{{ markText(mod.def, step.def, mark.key) }}</span>
                              </li>
                            }
                          </ul>
                        }
                      </div>
                    </div>
                  </div>
                }

                <div class="d-flex flex-wrap gap-3 pt-2">
                  <a class="btn btn-sm btn-primary" [routerLink]="mod.def.route">
                    {{ "MANUAL.TRY_IT" | translate }}
                  </a>
                  <button type="button" class="btn btn-sm btn-light-primary" (click)="setMode('video')">
                    {{ "MANUAL.WATCH_INSTEAD" | translate }}
                  </button>
                </div>
              }
            </div>
          }
        </section>
      }

      @if (!resolved().length) {
        <div class="card">
          <div class="card-body empty-state" data-testid="manual-empty">
            <p class="fs-5 text-muted mb-0">{{ "MANUAL.NO_RESULTS" | translate }}</p>
          </div>
        </div>
      }
    </div>
  </div>
</div>
